// Executive Cockpit — the first screen a business owner sees.
// Answers: How is my business doing? What requires my attention? What should I do today?
// NOT a CRUD homepage. Communicates control, clarity, and decision-making.

import { useMemo } from 'react';
import { Box, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import { Refresh } from 'iconsax-reactjs';

import { useBusinessData } from '../dashboard/useBusinessData';
import { num } from '../dashboard/kpis';
import { KpiRow } from '../dashboard/components/KpiCard';
import { AlertsPanel } from '../dashboard/components/AlertsPanel';
import { BusinessFeed, rowsToFeed } from '../dashboard/components/BusinessFeed';
import { SectionHeader } from '../dashboard/components/SectionHeader';
import { ChartCard, DashboardPieChart, MonthlyCashFlowChart, SalesByHourChart, WeeklyPerformanceChart, YearlyTrendChart } from '../dashboard/components/Charts';
import { DailyOverviewChart, MonthlyOverviewChart, PeriodSelectors, WeeklyTotals } from '../dashboard/components/PeriodCharts';
import { usePeriodCharts } from '../dashboard/usePeriodCharts';
import { getRuntimeConfig } from '../runtime';
import { t, formatDate } from '../i18n';
import { CrudRecord } from '../crud/types';

export function DashboardPage() {
  const runtime = getRuntimeConfig();
  const data = useBusinessData();
  const period = usePeriodCharts();
  const k = data.kpis;

  // Build feed entries.
  const recentSales = useMemo(
    () =>
      rowsToFeed(data.rows.sales, (s: CrudRecord) => ({
        id: s.id,
        title: String((s.customer as { name?: string; company?: string } | undefined)?.name ?? s.reference ?? 'Sale'),
        subtitle: s.reference ? String(s.reference) : undefined,
        amount: num(s.grand_total),
        date: s.date ? formatDate(s.date) : undefined,
        tone: 'positive'
      })),
    [data.rows.sales]
  );
  const recentPurchases = useMemo(
    () =>
      rowsToFeed(data.rows.purchases, (p: CrudRecord) => ({
        id: p.id,
        title: String((p.supplier as { name?: string; company?: string } | undefined)?.name ?? 'Purchase'),
        subtitle: p.reference ? String(p.reference) : undefined,
        amount: num(p.grand_total),
        date: p.date ? formatDate(p.date) : undefined,
        tone: 'negative'
      })),
    [data.rows.purchases]
  );
  const recentExpenses = useMemo(
    () =>
      rowsToFeed(data.rows.expenses, (e: CrudRecord) => ({
        id: e.id,
        title: String(e.title ?? e.reference ?? 'Expense'),
        subtitle: e.reference ? String(e.reference) : undefined,
        amount: num(e.amount),
        date: e.date ? formatDate(e.date) : undefined,
        tone: 'negative'
      })),
    [data.rows.expenses]
  );

  const primaryKpis = [k.revenueToday, k.revenueMonth, k.cash, k.inventory];
  const secondaryKpis = [k.receivables, k.payables, k.openCashRegisters, k.ordersWaiting, k.lowStock];

  if (data.loading) {
    return (
      <Stack spacing={3}>
        <KpiRow kpis={primaryKpis} loading />
        <KpiRow kpis={secondaryKpis} loading />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <SectionHeader
        title={`${runtime.appName}`}
        description={t('dashboard.description')}
        actionLabel={t('action.refresh')}
        actionIcon={Refresh}
        onAction={() => {
          data.refresh();
          void period.refresh();
        }}
      />

      {/* Primary KPI row */}
      <KpiRow kpis={primaryKpis} />

      {/* Secondary KPI row */}
      <KpiRow kpis={secondaryKpis} />

      {/* Alerts + Operational Center */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 2fr' }, gap: 2 }}>
        <Card>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              {t('dashboard.insightsAlerts')}
            </Typography>
            <AlertsPanel alerts={data.alerts} />
          </CardContent>
        </Card>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {t('dashboard.recentSales')}
              </Typography>
              <BusinessFeed entries={recentSales} emptyMessage={t('dashboard.noSales')} />
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {t('dashboard.recentPurchases')}
              </Typography>
              <BusinessFeed entries={recentPurchases} emptyMessage={t('dashboard.noPurchases')} />
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {t('dashboard.recentExpenses')}
              </Typography>
              <BusinessFeed entries={recentExpenses} emptyMessage={t('dashboard.noExpenses')} />
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Legacy dashboard parity: weekly per-type totals + month/year overview (ChartService) */}
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          {t('dashboard.thisWeek')}
        </Typography>
        <WeeklyTotals week={period.week} />
      </Box>
      <Card>
        <CardContent sx={{ p: 2.25 }}>
          <PeriodSelectors month={period.month} year={period.year} onMonth={period.setMonth} onYear={period.setYear} loading={period.loading} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, textAlign: 'center' }}>
                {t('dashboard.dailyOverview')}
              </Typography>
              <DailyOverviewChart data={period.monthChart} />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, textAlign: 'center' }}>
                {t('dashboard.monthlyOverview')}
              </Typography>
              <MonthlyOverviewChart data={period.yearChart} />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Charts — server-side aggregated data from ChartService */}
      <ChartCard title={t('dashboard.salesTrend')}>
        <WeeklyPerformanceChart data={data.dashboard?.week} />
      </ChartCard>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
        <ChartCard title={t('dashboard.cashFlow')}>
          <MonthlyCashFlowChart data={data.dashboard?.chart?.month} />
        </ChartCard>
        <ChartCard title={t('dashboard.yearlyTrend')}>
          <YearlyTrendChart data={data.dashboard?.chart?.year} />
        </ChartCard>
      </Box>
      <ChartCard title={t('dashboard.salesPeakHoursLastWeek')}>
        <SalesByHourChart data={data.charts.salesByHourLastSevenDays} />
      </ChartCard>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: 'repeat(3, 1fr)' }, gap: 2 }}>
        <ChartCard title={t('dashboard.salesByRegister')}>
          <DashboardPieChart data={data.charts.salesByRegister} />
        </ChartCard>
        <ChartCard title={t('dashboard.salesByCashier')}>
          <DashboardPieChart data={data.charts.salesByCashier} />
        </ChartCard>
        <ChartCard title={t('dashboard.salesByPaymentType')}>
          <DashboardPieChart data={data.charts.salesByPaymentType} />
        </ChartCard>
        <ChartCard title={t('dashboard.salesByItem')}>
          <DashboardPieChart data={data.charts.salesByItem} />
        </ChartCard>
        <ChartCard title={t('dashboard.salesByItemCategory')}>
          <DashboardPieChart data={data.charts.salesByItemCategory} />
        </ChartCard>
        <ChartCard title={t('dashboard.returnsByItem')}>
          <DashboardPieChart data={data.charts.returnsByItem} />
        </ChartCard>
      </Box>
    </Stack>
  );
}
